import type { SizeRow } from "./sizes";

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
export type Placed<T> = Rect & { item: T };
export interface AreaTotal {
  area: string;
  accounts: number;
  minutes: number;
  overflows: number;
}
export interface MapArea {
  area: string;
  gallons: number;
  minutes: number;
  swaps: number;
}
export interface TreeTile extends Rect {
  key: string;
  area: string;
  /** Null on the Other tile of an area. */
  row: SizeRow | null;
  gallons: number;
  minutes: number;
  accounts: number;
  noEstimate: boolean;
}
export interface TreeBlock extends Rect {
  area: string;
  gallons: number;
  minutes: number;
  tiles: TreeTile[];
}

export const NO_AREA = "No area";
export const PHONE_WIDTH = 640;
export const AREA_HEADER_PX = 24;
export const MIN_TILE_PHONE = 24;
export const MIN_TILE_DESKTOP = 12;
const MIN_BLOCK_AREA = 60 * 60;
const MAX_TILES = 1500;
const MIN_AREA_CAP = 20;
const PHONE_HEIGHT = 560;
const WIDE_HEIGHTS = { low: 420, high: 640, ratio: 0.55 };
const TILE_TEXT = { w: 64, h: 36 };
const TILE_CHANGE = { charPx: 8, paddingPx: 8 };
const FIT_PASSES = 4;
const MAX_HEIGHT = 3600;
const SWAP_ROOM_MARGIN = 1.25;
const LIFT = 1.5;
const MAX_LIFTS = 6;
const EPSILON = 1e-6;

/** The RdYlBu_r stops of matplotlib, from the least minutes to the most. */
const SCALE_STOPS = ["#313695", "#4575b4", "#74add1", "#abd9e9", "#e0f3f8", "#ffffbf", "#fee090", "#fdae61", "#f46d43", "#d73027", "#a50026"];
export const NO_ESTIMATE_FILL = "#8b929c";
export const SCALE_GRADIENT = `linear-gradient(to right, ${SCALE_STOPS.join(", ")})`;
const SCALE_TICKS = [0, 5, 20, 50];
const TICK_GAP = 0.06;
const TOP_TICK_GAP = 0.12;

const hexChannels = (hex: string) => [1, 3, 5].map((at) => parseInt(hex.slice(at, at + 2), 16));

/** The place of the minutes on the scale, 0 to 1. The square root makes small savings show. */
export const scalePosition = (minutes: number, top: number) => (top > 0 ? Math.sqrt(Math.min(Math.max(minutes, 0) / top, 1)) : 0);

export function scaleFill(minutes: number, top: number) {
  const place = scalePosition(minutes, top) * (SCALE_STOPS.length - 1);
  const low = Math.min(Math.floor(place), SCALE_STOPS.length - 2);
  const [from, to] = [hexChannels(SCALE_STOPS[low]), hexChannels(SCALE_STOPS[low + 1])];
  const mixed = from.map((channel, index) => Math.round(channel + (to[index] - channel) * (place - low)));
  return `#${mixed.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}

const luminance = (hex: string) => {
  const [r, g, b] = hexChannels(hex).map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export function contrast(a: string, b: string) {
  const [high, low] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (high + 0.05) / (low + 0.05);
}

/** Pure black or white: one of the two always has a contrast of 4.5:1 or more. */
export const inkOn = (fill: string) => (contrast(fill, "#ffffff") >= contrast(fill, "#000000") ? "#ffffff" : "#000000");

export const tileFill = (tile: Pick<TreeTile, "minutes" | "noEstimate">, top: number) => (tile.noEstimate ? NO_ESTIMATE_FILL : scaleFill(tile.minutes, top));

/** The largest saving of the rows. A drill passes all the rows of the page, so its scale stays the same. */
export const scaleTop = (rows: readonly SizeRow[]) => rows.reduce((top, row) => Math.max(top, row.saving?.minutes_month ?? 0), 0);

/** The ticks of the legend. A fixed tick that is too near its neighbour goes, so the labels do not touch. */
export function scaleTicks(top: number) {
  const ticks: { value: number; at: number }[] = [];
  for (const value of SCALE_TICKS) {
    const at = scalePosition(value, top);
    const last = ticks[ticks.length - 1];
    if (value < top && at <= 1 - TOP_TICK_GAP && (!last || at - last.at >= TICK_GAP)) ticks.push({ value, at });
  }
  if (ticks.length === 0) ticks.push({ value: 0, at: 0 });
  return Math.round(top) > 0 ? [...ticks, { value: top, at: 1 }] : ticks;
}

const worstRatio = (areas: readonly number[], side: number) => {
  const sum = areas.reduce((total, area) => total + area, 0);
  return Math.max((side * side * Math.max(...areas)) / (sum * sum), (sum * sum) / (side * side * Math.min(...areas)));
};

/** Squarified treemap: the largest value first, equal values in the given order. A row stops before an item gets shorter than `minLong`. */
export function squarify<T>(items: readonly T[], value: (item: T) => number, width: number, height: number, minLong = 0): Placed<T>[] {
  const sized = items.map((item) => ({ item, value: value(item) })).filter((entry) => entry.value > 0);
  const total = sized.reduce((sum, entry) => sum + entry.value, 0);
  if (!total || width <= 0 || height <= 0) return [];
  const scale = (width * height) / total;
  const queue = sized.map((entry) => ({ item: entry.item, area: entry.value * scale })).sort((a, b) => b.area - a.area);
  const placed: Placed<T>[] = [];
  let [x, y, w, h] = [0, 0, width, height];
  let row: typeof queue = [];
  const flush = () => {
    const sum = row.reduce((all, entry) => all + entry.area, 0);
    const across = w >= h;
    const thick = sum / (across ? h : w);
    let along = across ? y : x;
    for (const { item, area } of row) {
      const long = area / thick;
      placed.push(across ? { item, x, y: along, w: thick, h: long } : { item, x: along, y, w: long, h: thick });
      along += long;
    }
    if (across) [x, w] = [x + thick, w - thick];
    else [y, h] = [y + thick, h - thick];
    row = [];
  };
  for (const entry of queue) {
    const side = Math.min(w, h);
    const areas = row.map((held) => held.area);
    const sum = areas.reduce((total, area) => total + area, entry.area);
    const tooShort = (entry.area * side) / sum < minLong;
    if (row.length && (tooShort || worstRatio([...areas, entry.area], side) > worstRatio(areas, side))) flush();
    row.push(entry);
  }
  flush();
  return placed;
}

const single = (row: SizeRow) => ({
  key: row.account.id,
  row,
  gallons: row.account.capacity_gal,
  minutes: Math.max(row.saving?.minutes_month ?? 0, 0),
  accounts: 1,
  noEstimate: row.saving === null,
});

/** The tiles of one area in its box. Only a tile with no saving goes into the Other tile; a tile with a saving gets a minimum area. */
function placeTiles(area: string, rows: readonly SizeRow[], box: Rect, cap: number, minSide: number): TreeTile[] {
  const tiles = rows.map(single);
  const swaps = tiles.filter((tile) => tile.minutes > 0);
  const quiet = tiles.filter((tile) => tile.minutes <= 0);
  const place = (keep: number, least: number) => {
    const rest = quiet.slice(keep);
    const other = { key: `other:${area}`, row: null, gallons: rest.reduce((sum, tile) => sum + tile.gallons, 0), minutes: 0, accounts: rest.length, noEstimate: rest.every((tile) => tile.noEstimate) };
    const shown = [...swaps, ...quiet.slice(0, keep), ...(rest.length ? [other] : [])];
    return squarify(shown, (tile) => (tile.row && tile.minutes <= 0 ? tile.gallons : Math.max(tile.gallons, least)), box.w, box.h, minSide);
  };
  const gallons = tiles.reduce((sum, tile) => sum + tile.gallons, 0);
  let least = (tileRoom(minSide) * gallons) / (box.w * box.h);
  let lifts = swaps.length ? MAX_LIFTS : 0;
  let keep = Math.min(quiet.length, cap);
  for (;;) {
    if (quiet.length - keep === 1 && keep > 0) keep -= 1;
    const placed = place(keep, least);
    const small = placed.some((tile) => Math.min(tile.w, tile.h) < minSide - EPSILON);
    if (!small || rows.length === 1 || (keep === 0 && lifts === 0)) {
      return placed.map(({ item, x, y, w, h }) => ({ ...item, area, x: box.x + x, y: box.y + y, w, h }));
    }
    if (keep > 0) keep = Math.floor(keep * 0.75);
    else {
      lifts -= 1;
      least *= LIFT;
    }
  }
}

/** The area blocks. Each block also pays for its heading, so tile areas stay comparable across blocks. */
function placeBlocks<T extends { gallons: number; room: number }>(groups: readonly T[], width: number, height: number): Placed<T>[] {
  const weights = new Map(groups.map((group) => [group, group.gallons]));
  const weightOf = (group: T) => weights.get(group) ?? 0;
  let blocks = squarify(groups, weightOf, width, height);
  const total = groups.reduce((sum, group) => sum + group.gallons, 0);
  let scale = (width * height) / total;
  for (let pass = 0; pass < FIT_PASSES; pass += 1) {
    const headers = blocks.reduce((sum, block) => sum + block.w * AREA_HEADER_PX, 0);
    const tight = groups.filter((group) => group.gallons * scale < group.room);
    const held = tight.reduce((sum, group) => sum + group.room, 0);
    const heldGallons = tight.reduce((sum, group) => sum + group.gallons, 0);
    scale = Math.max(width * height - headers - held, 0) / Math.max(total - heldGallons, 1);
    for (const block of blocks) {
      weights.set(block.item, Math.max(Math.max(block.item.gallons * scale, block.item.room) + block.w * AREA_HEADER_PX, MIN_BLOCK_AREA));
    }
    blocks = squarify(groups, weightOf, width, height);
  }
  return blocks;
}

/** The gallons and the swaps of each area with a container, the most gallons first. */
export function mapAreas(rows: readonly SizeRow[]): MapArea[] {
  const areas = new Map<string, MapArea>();
  for (const { account, saving } of rows) {
    if (account.capacity_gal <= 0) continue;
    const area = account.plan?.area ?? NO_AREA;
    const held = areas.get(area) ?? { area, gallons: 0, minutes: 0, swaps: 0 };
    const minutes = Math.max(saving?.minutes_month ?? 0, 0);
    held.gallons += account.capacity_gal;
    held.minutes += minutes;
    held.swaps += minutes > 0 ? 1 : 0;
    areas.set(area, held);
  }
  return [...areas.values()].sort((a, b) => b.gallons - a.gallons || a.area.localeCompare(b.area));
}

/** The treemap of the containers: one block for each area, one tile for each account with a container. */
export function treemap(rows: readonly SizeRow[], width: number, height: number, minSide: number): TreeBlock[] {
  const byArea = new Map<string, SizeRow[]>();
  for (const row of rows) {
    if (row.account.capacity_gal <= 0) continue;
    const area = row.account.plan?.area ?? NO_AREA;
    const held = byArea.get(area);
    if (held) held.push(row);
    else byArea.set(area, [row]);
  }
  const areas = mapAreas(rows);
  const groups = areas.map((area) => ({
    ...area,
    room: area.swaps * tileRoom(minSide),
    rows: (byArea.get(area.area) ?? []).sort((a, b) => b.account.capacity_gal - a.account.capacity_gal || a.account.number.localeCompare(b.account.number)),
  }));
  const swaps = areas.reduce((sum, area) => sum + area.swaps, 0);
  const cap = Math.max(MIN_AREA_CAP, Math.floor(Math.max(MAX_TILES - swaps, 0) / Math.max(groups.length, 1)));
  return placeBlocks(groups, width, height).map(({ item, x, y, w, h }) => {
    const inner = { x, y: y + AREA_HEADER_PX, w, h: Math.max(h - AREA_HEADER_PX, 1) };
    return { area: item.area, gallons: item.gallons, minutes: item.minutes, x, y, w, h, tiles: placeTiles(item.area, item.rows, inner, cap, minSide) };
  });
}

/** The height of the treemap. Many areas or swaps make it taller, so each tile keeps its tap room, up to a limit. */
export function treemapHeight(width: number, areas: number, swaps = 0) {
  const base = width < PHONE_WIDTH ? PHONE_HEIGHT : Math.min(WIDE_HEIGHTS.high, Math.max(WIDE_HEIGHTS.low, width * WIDE_HEIGHTS.ratio));
  if (width <= 0) return Math.ceil(base);
  const forAreas = (areas * MIN_BLOCK_AREA * 1.5) / width;
  const forSwaps = forAreas + (swaps * tileRoom(tileMinSide(width)) * SWAP_ROOM_MARGIN) / width;
  return Math.ceil(Math.max(base, forAreas, Math.min(forSwaps, MAX_HEIGHT)));
}

const STEP_KEYS: Record<string, 1 | -1> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

/** The index that a key moves to, kept in the list, or null for a key that does not move. */
export function nextTile(key: string, at: number, count: number): number | null {
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  const step = STEP_KEYS[key];
  return step ? Math.min(Math.max(at + step, 0), count - 1) : null;
}

export const tileMinSide = (width: number) => (width < PHONE_WIDTH ? MIN_TILE_PHONE : MIN_TILE_DESKTOP);
const tileRoom = (minSide: number) => (minSide * 1.4) ** 2;
export const tileHasText = (tile: Rect) => tile.w >= TILE_TEXT.w && tile.h >= TILE_TEXT.h;
export const tileFitsChange = (tile: Rect, text: string) => tileHasText(tile) && tile.w >= text.length * TILE_CHANGE.charPx + TILE_CHANGE.paddingPx;
/** The size change that fits a tile: with the unit, else without it, else nothing. */
export const tileChange = (tile: Rect, text: string) => [text, text.replace(/ gal$/, "")].find((line) => line && tileFitsChange(tile, line)) ?? "";

/** The minutes and the overflows of the swaps of each area, the most minutes first. */
export function areaTotals(rows: readonly SizeRow[]): AreaTotal[] {
  const totals = new Map<string, AreaTotal>();
  for (const { account, saving } of rows) {
    if (!saving || saving.minutes_month <= 0) continue;
    const area = account.plan?.area ?? NO_AREA;
    const total = totals.get(area) ?? { area, accounts: 0, minutes: 0, overflows: 0 };
    total.accounts += 1;
    total.minutes += saving.minutes_month;
    total.overflows += saving.overflows_month;
    totals.set(area, total);
  }
  return [...totals.values()].sort((a, b) => b.minutes - a.minutes || a.area.localeCompare(b.area));
}
